import { useState, useRef, useEffect, useCallback } from 'react';

/**
 * Custom hook for pull-to-refresh functionality
 * @param {Function} onRefresh - Callback function to execute when refresh is triggered
 * @param {Object} options - Configuration options
 * @param {number} options.threshold - Distance in pixels to trigger refresh (default: 120)
 * @param {number} options.resistance - Resistance factor for pull (default: 3.5)
 * @param {number} options.visibilityThreshold - Distance in pixels before indicator appears (default: 40)
 * @param {boolean} options.disabled - Disable pull-to-refresh (default: false)
 */
export const usePullToRefresh = (onRefresh, options = {}) => {
  const {
    threshold = 120,
    resistance = 3.5,
    visibilityThreshold = 40,
    disabled = false
  } = options;

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const startY = useRef(0);
  const currentY = useRef(0);
  const isPulling = useRef(false);
  const containerRef = useRef(null);

  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    try {
      await onRefresh();
    } catch (error) {
      console.error('Error refreshing:', error);
    } finally {
      setIsRefreshing(false);
      setPullDistance(0);
    }
  }, [onRefresh, isRefreshing]);

  // Helper function to check if touch is within an exclusion zone: FilterBar, or a
  // bottom-sheet overlay such as the softwall "Sign up or Log in" sheet. Treating
  // taps inside a sheet as a pull and calling preventDefault() on their touchmoves
  // can make Android Chrome drop the click on the sheet's buttons (HYK-1136).
  const isWithinFilterBar = useCallback((targetElement) => {
    if (!targetElement?.closest) return false;

    const excluded = targetElement.closest('.filter-bar, .filter-news-bar, .contribution-section');
    return excluded !== null;
  }, []);

  const handleTouchStart = useCallback((e) => {
    if (disabled || isRefreshing) return;

    // Only trigger if at the top of the scrollable area
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
    if (scrollTop > 10) return; // Allow small threshold for floating point issues

    // Check if touch started within FilterBar exclusion zone
    const targetElement = e.target;
    if (isWithinFilterBar(targetElement)) {
      return; // Don't trigger pull-to-refresh when touching FilterBar
    }

    const touch = e.touches?.[0] || e;
    startY.current = touch.clientY;
    currentY.current = touch.clientY;
    isPulling.current = true;
  }, [disabled, isRefreshing, isWithinFilterBar]);

  const handleTouchMove = useCallback((e) => {
    if (!isPulling.current || disabled || isRefreshing) return;

    const touch = e.touches?.[0] || e;
    currentY.current = touch.clientY;
    const deltaY = currentY.current - startY.current;

    // Only prevent default if we're pulling down and at the top
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
    if (deltaY > 0 && scrollTop <= 10) {
      e.preventDefault(); // Prevent default scroll behavior
      const distance = Math.min(deltaY / resistance, threshold * 1.5);
      setPullDistance(distance);
    } else if (deltaY <= 0) {
      setPullDistance(0);
      isPulling.current = false;
    }
  }, [disabled, isRefreshing, resistance, threshold]);

  const handleTouchEnd = useCallback(() => {
    if (!isPulling.current) return;

    isPulling.current = false;

    if (pullDistance >= threshold && !isRefreshing) {
      handleRefresh();
    } else {
      setPullDistance(0);
    }
  }, [pullDistance, threshold, isRefreshing, handleRefresh]);

  // Mouse events for desktop support
  const handleMouseDown = useCallback((e) => {
    if (disabled || isRefreshing) return;

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
    if (scrollTop > 10) return;

    // Check if click started within FilterBar exclusion zone
    const targetElement = e.target;
    if (isWithinFilterBar(targetElement)) {
      return; // Don't trigger pull-to-refresh when clicking FilterBar
    }

    startY.current = e.clientY;
    currentY.current = e.clientY;
    isPulling.current = true;
  }, [disabled, isRefreshing, isWithinFilterBar]);

  const handleMouseMove = useCallback((e) => {
    if (!isPulling.current || disabled || isRefreshing) return;

    currentY.current = e.clientY;
    const deltaY = currentY.current - startY.current;

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
    if (deltaY > 0 && scrollTop <= 10) {
      e.preventDefault();
      const distance = Math.min(deltaY / resistance, threshold * 1.5);
      setPullDistance(distance);
    } else if (deltaY <= 0) {
      setPullDistance(0);
      isPulling.current = false;
    }
  }, [disabled, isRefreshing, resistance, threshold]);

  const handleMouseUp = useCallback(() => {
    if (!isPulling.current) return;

    isPulling.current = false;

    if (pullDistance >= threshold && !isRefreshing) {
      handleRefresh();
    } else {
      setPullDistance(0);
    }
  }, [pullDistance, threshold, isRefreshing, handleRefresh]);

  // Attach event listeners
  useEffect(() => {
    // Touch events - attach to window/document for better coverage
    window.addEventListener('touchstart', handleTouchStart, { passive: false });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);

    // Mouse events for desktop
    window.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd, handleMouseDown, handleMouseMove, handleMouseUp]);

  // Calculate pull progress (0 to 1)
  const pullProgress = Math.min(pullDistance / threshold, 1);
  const shouldTrigger = pullDistance >= threshold;
  const shouldShowIndicator = pullDistance >= visibilityThreshold;

  return {
    containerRef,
    isRefreshing,
    pullDistance,
    pullProgress,
    shouldTrigger,
    shouldShowIndicator
  };
};

